import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router';
import { useAdminUsers, useStudioBadges } from '../api/hooks';
import { buildLibrarySearch, useLibraryState } from '../state/libraryParams';
import { useSession } from '../state/session';
import { ProfileDialog } from './dialogs/ProfileDialog';
import { Icon, type IconName } from './Icon';
import { SearchBox } from './SearchBox';
import { SourceMenu } from './SourceMenu';
import { UserMenu } from './UserMenu';

/** Logo + area name ("Photos", "Studio", "Admin"). */
export function Brand({ to = '/', label = 'Photos' }: { to?: string; label?: string }) {
  return (
    <Link className="brand" to={to}>
      <img className="logo" alt="JBCH" src="/brand/jbch-emblem-180.png" />
      <b>{label}</b>
    </Link>
  );
}

export type Area = 'photos' | 'studio' | 'admin';

/**
 * Switch between the areas the user can use: Photos · Studio · Admin, the current one highlighted. People who can only
 * use Photos see nothing. Admin carries the pending access-request count; Studio the open removal requests the person can
 * review (media).
 */
export function AreaSwitcher({ current }: { current: Area }) {
  const { me } = useSession();
  const users = useAdminUsers(me.capabilities.admin);
  const pending = (users.data ?? []).filter((u) => u.status === 'pending').length;
  const badges = useStudioBadges(me.capabilities.media);
  const removals = me.capabilities.media ? (badges.data?.removalRequests ?? 0) : 0;
  const badge = (key: Area) => (key === 'admin' ? pending : key === 'studio' ? removals : 0);
  const label = (a: { key: Area; label: string }) =>
    a.key === 'admin' && pending ? `Admin, ${pending} pending` : a.key === 'studio' && removals ? `Studio, ${removals} removal ${removals === 1 ? 'request' : 'requests'}` : a.label;
  if (!me.capabilities.studio && !me.capabilities.admin) return null;
  const areas: { key: Area; to: string; label: string; icon: IconName; show: boolean }[] = [
    { key: 'photos', to: '/', label: 'Photos', icon: 'grid', show: true },
    { key: 'studio', to: '/studio', label: 'Studio', icon: 'image', show: me.capabilities.studio },
    { key: 'admin', to: '/admin', label: 'Admin', icon: 'settings', show: me.capabilities.admin },
  ];
  return (
    <nav className="seg light areas" aria-label="Areas">
      {areas
        .filter((a) => a.show)
        .map((a) => (
          <Link
            key={a.key}
            to={a.to}
            className={a.key === current ? 'on' : ''}
            aria-current={a.key === current ? 'page' : undefined}
            aria-label={label(a)}
            title={a.label}
          >
            <Icon name={a.icon} />
            <span>{a.label}</span>
            {badge(a.key) > 0 && <span className="sbadge">{badge(a.key)}</span>}
          </Link>
        ))}
    </nav>
  );
}

/** The avatar menu for the signed-in user (every area). */
export function SessionUserMenu({ onProfile }: { onProfile(): void }) {
  const { user, roleLabel, tagIndex } = useSession();
  return (
    <UserMenu
      name={user.name}
      subtitle={[user.church && tagIndex.labelOf(user.church), roleLabel].filter(Boolean).join(' · ')}
      onProfile={onProfile}
    />
  );
}

/** Sets --hdr to the header's real height (sticky elements below offset by it). */
export function useHeaderHeight(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--hdr', `${el.offsetHeight}px`);
    set();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
}

export function Header({ onMenu }: { onMenu(): void }) {
  const { me } = useSession();
  const { state } = useLibraryState();
  const home = `/${buildLibrarySearch({ ...state, tab: 'all', tags: [], orient: 'all', picksOnly: false })}`;
  const ref = useRef<HTMLElement>(null);
  const [profile, setProfile] = useState(false);

  // Everything sticky below the header (sidebar, floating banner, date rows) offsets by its real height.
  useHeaderHeight(ref);

  return (
    <header className="top" ref={ref}>
      <div className="top-row">
        <div className="top-left">
          <button type="button" className="menu-btn" id="menuBtn" aria-label="Open navigation" onClick={onMenu}>
            <Icon name="menu" />
          </button>
          <Brand to={home} />
        </div>
        <SourceMenu variant="title" />
        <div className="actions">
          <AreaSwitcher current="photos" />
          <SessionUserMenu onProfile={() => setProfile(true)} />
        </div>
      </div>
      <div className="top-sub">
        <SearchBox />
        {me.capabilities.media && (
          <Link className="btn primary up-btn" to="/studio/upload">
            <Icon name="upload" />
            <span className="hide-sm">Upload</span>
          </Link>
        )}
      </div>
      {profile && <ProfileDialog onClose={() => setProfile(false)} />}
    </header>
  );
}
